import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
  IHealthHubData,
  IHealthHubInteractionType,
  IHealthHubPlaylistResult,
  IHealthHubPlaylistVideo,
  IHealthHubResultType,
} from "@/models/ApiRequestResponse/patient";

import styles from "./index.module.css";

import VideoPlayListSection from "@/views/Patient/HealthHub/VideoPlaylistSection";
import VideoActions from "@/views/Patient/VideoDetails/VideoActions";
import GetImages from "@/components/GetImages";

import { useAppSelector } from "@/store/hooks";

type ShortVideoSectionProps = {
  videoData: IHealthHubPlaylistVideo | undefined;
};

export const getYoutubeEmbedUrl = (url: string): string => {
  const regExp =
    /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/;
  const match = url.match(regExp);

  if (match && match[2].length === 11) {
    return `https://www.youtube.com/embed/${match[2]}?autoplay=1`;
  }

  return "";
};

export const getCurrentVideoPlaylist = (
  videoId: string,
  data: IHealthHubData
) => {
  const playlists = data.result.filter(
    (i): i is IHealthHubPlaylistResult =>
      i.type === IHealthHubResultType.PLAYLIST ||
      i.type === IHealthHubResultType.SHORT
  );

  const currentVideoPlaylist = playlists.find((i) =>
    i.data.some((v) => v.id === videoId)
  );

  if (!currentVideoPlaylist) {
    return undefined;
  }

  const relatedVideos = currentVideoPlaylist.data.filter(
    (i) => i.id !== videoId
  );

  return {
    id: currentVideoPlaylist.id,
    name: currentVideoPlaylist.name,
    type: currentVideoPlaylist.type,
    data: relatedVideos,
  };
};

const ShortVideoSection: React.FC<ShortVideoSectionProps> = ({ videoData }) => {
  const { data } = useAppSelector((state) => state.healthHub);

  const [currentVideoPlaylist, setCurrentVideoPlaylist] = useState(() =>
    getCurrentVideoPlaylist(videoData?.id!, data)
  );
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (videoData) {
      const newPlaylist = getCurrentVideoPlaylist(videoData?.id!, data);
      setCurrentVideoPlaylist(newPlaylist);
    }
  }, [videoData, data]);

  const { name, video_url, description } = videoData ?? {};

  const embedUrl = video_url ? getYoutubeEmbedUrl(video_url) : "";

  return (
    <Grid className={styles.shortVideoSectionContainer}>
      <Grid>
        <Grid
          className={styles.videoContainer}
          sx={{
            height: { xs: "100%", md: "50rem", lg: "67.2rem" },
            width: { xs: "100%", sm: "20rem", md: "30rem", lg: "39.2rem" },
            position: "relative",
          }}
        >
          <div className={styles.closeIconContainer} onClick={handleClose}>
            <GetImages name="CloseIcon" width="14" height="14" />
          </div>
          {embedUrl && (
            <iframe
              src={embedUrl}
              title={name}
              className={styles.videoPlayer}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="autoplay"
              allowFullScreen
            />
          )}
          <div className={styles.videoPlayerOverlay}></div>
        </Grid>
      </Grid>
      <Grid className={styles.rightContainer}>
        <Grid className={styles.contentContainer}>
          <Grid className={styles.videoContentContainer}>
            <Typography variant="h6" className={styles.videoTitle}>
              {name}
            </Typography>
            <Typography variant="body1" className={styles.videoDescription}>
              {description}
            </Typography>
          </Grid>
          <VideoActions
            isLiked={videoData?.user_interaction?.some(
              (interaction) =>
                interaction.type === IHealthHubInteractionType.LIKE
            )}
            isFavourited={videoData?.user_interaction?.some(
              (interaction) =>
                interaction.type === IHealthHubInteractionType.FAVOURITE
            )}
            videoId={videoData?.id!}
            likesCount={videoData?.likes}
          />
        </Grid>
        <Grid>
          <Grid className={styles.relatedVideosWrapper}>
            <VideoPlayListSection
              isSection={false}
              className={styles.relatedVideosContainer}
              data={{
                id: currentVideoPlaylist?.id || "",
                name: currentVideoPlaylist?.name || "",
                type: IHealthHubResultType.SHORT,
                data: currentVideoPlaylist?.data || [],
              }}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ShortVideoSection;
